/**
 * BookingSuccessModal.jsx
 * Module: common / PaymentSection
 *
 * Shared "Booking Confirmed!" modal shown after a booking is finalized —
 * checkmark header, a 12s countdown ring, and an auto-redirect to
 * Booking History (with a "Redirect Now" button to skip the wait).
 * Extracted from NewBookingFlowContent.jsx's air-cargo success modal so
 * other cargo-type flows (ocean, …) share the exact same UX instead of
 * re-implementing it.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * @param {Object} props
 * @param {boolean} props.visible - whether the modal is shown; the countdown
 *   resets to `seconds` each time this flips from false to true
 * @param {string} [props.redirectTo] - route to navigate to when the
 *   countdown reaches 0 or "Redirect Now" is clicked (default "/booking-history")
 * @param {number} [props.seconds] - countdown duration in seconds (default 12)
 * @param {string} [props.title] - header line (default "Booking Confirmed!")
 * @param {string} [props.subtitle] - line under the header
 * @param {string} [props.message] - text inside the bordered message box
 */
const BookingSuccessModal = ({
  visible,
  redirectTo = "/booking-history",
  seconds = 12,
  title = "Booking Confirmed!",
  subtitle = "Your shipment is all set",
  message = "Booking confirmed successfully.",
}) => {
  const [countdown, setCountdown] = useState(seconds);
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) setCountdown(seconds);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    if (countdown === 0) {
      navigate(redirectTo);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Green header */}
        <div className="relative bg-gradient-to-b from-[#e6f9ee] to-[#f0fdf4] pt-10 pb-6 flex flex-col items-center">
          {/* Confetti dots */}
          <span className="absolute top-4 left-6 w-3 h-3 rounded-full bg-orange-400 opacity-80" />
          <span className="absolute top-8 left-16 w-2 h-2 rounded-full bg-blue-400 opacity-70" />
          <span className="absolute top-4 right-6 w-2.5 h-2.5 rounded-full bg-green-500 opacity-80" />
          <span className="absolute top-10 right-16 w-2 h-2 rounded-full bg-yellow-400 opacity-70" />
          <span className="absolute bottom-8 left-10 w-2 h-2 rounded-full bg-pink-400 opacity-70" />
          <span className="absolute bottom-6 right-10 w-2 h-2 rounded-full bg-blue-500 opacity-60" />
          <span className="absolute top-6 left-1/3 w-1.5 h-1.5 rounded-full bg-yellow-500 opacity-70" />
          <span className="absolute bottom-10 right-1/3 w-1.5 h-1.5 rounded-full bg-green-400 opacity-60" />
          {/* Checkmark circle */}
          <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold text-green-700 mb-1">{title}</h2>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>

        {/* Message */}
        <div className="px-6 py-5">
          <div className="border border-gray-200 rounded-xl px-4 py-4 text-center mb-5">
            <p className="text-sm text-green-600 font-medium">{message}</p>
          </div>

          {/* Countdown */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative w-14 h-14 shrink-0">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="20" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                <circle
                  cx="24" cy="24" r="20" fill="none"
                  stroke="#22c55e" strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={125.66}
                  strokeDashoffset={125.66 * (1 - countdown / seconds)}
                  style={{ transition: "stroke-dashoffset 0.9s linear" }}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">{countdown}</span>
            </div>
            <p className="text-sm text-gray-600">
              Redirecting to dashboard in <strong>{countdown}s</strong>
            </p>
          </div>

          {/* Button */}
          <button
            onClick={() => navigate(redirectTo)}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white font-bold py-3 rounded-xl text-base transition-all"
          >
            Redirect Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessModal;
